// @flow
import React, { Component } from "react";
import classNames from "classnames";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import Button from "@material-ui/core/Button";
import Paper from "@material-ui/core/Paper";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";
import Typography from "@material-ui/core/Typography";
import FastForwardIcon from "@material-ui/icons/FastForward";
import FastRewindIcon from "@material-ui/icons/FastRewind";
import KeyboardArrowLeftIcon from "@material-ui/icons/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@material-ui/icons/KeyboardArrowRight";
import { withStyles } from "@material-ui/core/styles";

import type { DictionaryType } from "../../types/GeneralTypes";
import type { StyledTextFieldClassesType } from "./StyledTextField";
import type { IconStylesClassesType } from "../../styles/iconStyles";
import type { ButtonStylesClassesType } from "../../styles/buttonStyles";
import { InputStyles } from "./StyledTextField";
import { iconStyles } from "../../styles/iconStyles";
import { buttonStyles } from "../../styles/buttonStyles";
import SingleCheckBox from "./SingleCheckBox";

const styles = (theme: DictionaryType): DictionaryType => ({
  ...InputStyles(theme),
  ...iconStyles(theme),
  ...buttonStyles(theme),
  listRoot: {
    padding: 0,
    marginBottom: theme.spacing.unit * 2,
    marginTop: theme.spacing.unit * 4,
    display: "flex",
    alignItems: "center"
  },
  listBox: {
    display: "flex",
    flex: "1 1",
    flexDirection: "column",
    alignSelf: "stretch"
  },
  listActions: {
    display: "flex",
    flex: "0 0 auto",
    flexDirection: "column",
    margin: "0 10px"
  },
  paper: {
    width: "100%",
    height: "230px",
    overflow: "auto"
  },
  invalid: {
    border: "1px solid " + theme.palette.error.main
  },
  title: {
    textAlign: "center",
    height: "24px"
  },
  buttonMargin: {
    marginBottom: "5px"
  },
  listIcon: {
    marginRight: "0px"
  },
  noPadding: {
    paddingTop: 0,
    paddingBottom: 0
  }
});

function not(a: Array<OptionType>, b: Array<OptionType>): Array<OptionType> {
  return a.filter(
    (item: *): * =>
      b.findIndex((x: OptionType): Function => x.value === item.value) === -1
  );
}

function intersection(
  a: Array<OptionType>,
  b: Array<OptionType>
): Array<OptionType> {
  return a.filter(
    (item: *): * =>
      b.findIndex((x: OptionType): Function => x.value === item.value) !== -1
  );
}

type OptionType = {
  label: string,
  value: string
};

type TransferListPropsType = {
  classes: {
    ...StyledTextFieldClassesType,
    ...IconStylesClassesType,
    ...ButtonStylesClassesType,
    listRoot: DictionaryType,
    listBox: DictionaryType,
    listActions: DictionaryType,
    paper: DictionaryType,
    invalid: DictionaryType,
    title: DictionaryType,
    buttonMargin: DictionaryType,
    listIcon: DictionaryType
  },
  label: string,
  name: string,
  options: Array<OptionType>,
  values: Array<string>,
  onChange: Function,
  onItemClick: Function,
  isRequired?: boolean,
  isInvalid?: boolean,
  isDisabled?: boolean
};

type TransferListStatesType = {
  checked: Array<OptionType>,
  left: Array<OptionType>,
  right: Array<OptionType>
};

class TransferList extends Component<
  TransferListPropsType,
  TransferListStatesType
> {
  constructor(props: TransferListPropsType) {
    super(props);
    this.state = {
      checked: [],
      left: [],
      right: []
    };
  }

  componentDidMount() {
    this.getListFromOptions(this.props.options, this.props.values);
  }

  componentDidUpdate(prevProps: TransferListPropsType) {
    if (
      prevProps.options !== this.props.options ||
      prevProps.values !== this.props.values
    ) {
      this.getListFromOptions(this.props.options, this.props.values);
    }
  }

  getListFromOptions = (options: Array<OptionType>, values: Array<string>) => {
    const left = options.filter(
      (item: *): * => values.indexOf(item.value) === -1
    );
    const right = options.filter(
      (item: *): * => values.indexOf(item.value) !== -1
    );
    this.setState({ left: left, right: right });
  };

  handleToggle = (item: OptionType): * => () => {
    const currentIndex = this.state.checked.findIndex(
      (x: OptionType): Function => x.value === item.value
    );
    const newChecked = [...this.state.checked];

    if (currentIndex === -1) {
      newChecked.push(item);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    this.setState({ checked: newChecked });
  };

  leftChecked = (): * => {
    return intersection(this.state.checked, this.state.left);
  };

  rightChecked = (): * => {
    return intersection(this.state.checked, this.state.right);
  };

  handleAllRight = () => {
    this.setState(
      {
        right: this.state.right.concat(this.state.left),
        left: []
      },
      this.syncChange
    );
  };

  handleCheckedRight = () => {
    const leftChecked = this.leftChecked();

    this.setState(
      {
        right: this.state.right.concat(leftChecked),
        left: not(this.state.left, leftChecked),
        checked: not(this.state.checked, leftChecked)
      },
      this.syncChange
    );
  };

  handleCheckedLeft = () => {
    const rightChecked = this.rightChecked();

    this.setState(
      {
        left: this.state.left.concat(rightChecked),
        right: not(this.state.right, rightChecked),
        checked: not(this.state.checked, rightChecked)
      },
      this.syncChange
    );
  };

  handleAllLeft = () => {
    this.setState(
      {
        left: this.state.left.concat(this.state.right),
        right: []
      },
      this.syncChange
    );
  };

  syncChange = (): * => {
    const values = this.state.right.map((item: OptionType): * => item.value);
    this.props.onChange(this.props.name, values);
  };

  singleItemClick = (value: string): * => (e: SyntheticInputEvent<*>): * => {
    if (e.target.tagName !== "INPUT") {
      this.props.onItemClick && this.props.onItemClick(value);
    }
  };

  render(): React$Element<*> {
    const classes = this.props.classes;

    const customList = (items: Array<OptionType>): React$Element<*> => (
      <Paper
        className={classNames(
          classes.paper,
          this.props.isInvalid && classes.invalid
        )}
      >
        <List dense component="div">
          {items.map(
            (item: OptionType): * => {
              return (
                <ListItem
                  key={item.value}
                  button
                  dense
                  disableGutters
                  classes={{
                    dense: classes.noPadding
                  }}
                  onClick={this.singleItemClick(item.value)}
                  disabled={this.props.isDisabled}
                >
                  <ListItemIcon className={classes.listIcon}>
                    <SingleCheckBox
                      name={this.props.name}
                      value={
                        this.state.checked.findIndex(
                          (x: *): * => x.value === item.value
                        ) !== -1
                      }
                      onChange={this.handleToggle(item)}
                    />
                  </ListItemIcon>
                  <ListItemText primary={item.label} />
                </ListItem>
              );
            }
          )}
          <ListItem />
        </List>
      </Paper>
    );

    return (
      <FormControl fullWidth={true} disabled={this.props.isDisabled}>
        {this.props.label !== "" && (
          <InputLabel
            htmlFor={this.props.name}
            required={this.props.isRequired}
            error={this.props.isInvalid}
            shrink={true}
            classes={{
              root: classes.greenLabel,
              formControl: classes.textFieldFormLabel,
              focused: classes.labelFocused,
              error: classes.labelError,
              disabled: classes.labelDisabled
            }}
          >
            {this.props.label}
          </InputLabel>
        )}
        <div className={classes.listRoot}>
          <div className={classes.listBox}>
            <div className={classes.title}>
              <Typography variant="body2">Available</Typography>
            </div>
            {customList(this.state.left)}
          </div>
          <div className={classes.listActions}>
            <Button
              variant="contained"
              size="small"
              color="primary"
              className={classNames(classes.lightBtn, classes.buttonMargin)}
              onClick={this.handleAllRight}
              disabled={this.state.left.length === 0 || this.props.isDisabled}
              aria-label="move all right"
            >
              <FastForwardIcon className={classes.greenIcon} />
            </Button>
            <Button
              variant="contained"
              size="small"
              color="primary"
              className={classNames(classes.lightBtn, classes.buttonMargin)}
              onClick={this.handleCheckedRight}
              disabled={
                this.leftChecked().length === 0 || this.props.isDisabled
              }
              aria-label="move selected right"
            >
              <KeyboardArrowRightIcon className={classes.greenIcon} />
            </Button>
            <Button
              variant="contained"
              size="small"
              color="primary"
              className={classNames(classes.lightBtn, classes.buttonMargin)}
              onClick={this.handleCheckedLeft}
              disabled={
                this.rightChecked().length === 0 || this.props.isDisabled
              }
              aria-label="move selected left"
            >
              <KeyboardArrowLeftIcon className={classes.greenIcon} />
            </Button>
            <Button
              variant="contained"
              size="small"
              color="primary"
              className={classNames(classes.lightBtn, classes.buttonMargin)}
              onClick={this.handleAllLeft}
              disabled={this.state.right.length === 0 || this.props.isDisabled}
              aria-label="move all left"
            >
              <FastRewindIcon className={classes.greenIcon} />
            </Button>
          </div>
          <div className={classes.listBox}>
            <div className={classes.title}>
              <Typography variant="body2">Selected</Typography>
            </div>
            {customList(this.state.right)}
          </div>
        </div>
      </FormControl>
    );
  }
}

export default withStyles(styles)(TransferList);
